import React from "react";
import { Chip, ListSubheader, MenuItem, Typography } from "@material-ui/core";
import Cancel from "@material-ui/icons/Cancel";
import { HeadingType, ItemIdType, ItemType } from "../types";

export const buildMenuItems = (
  rootId: string,
  menuItems: (HeadingType | ItemType)[],
  safeValue: any
) => {
  return menuItems.length
    ? menuItems.map((item, index) => {
        const itemAsItem = item as ItemType;
        const itemAsHeading = item as HeadingType;
        const isHeading = !!itemAsHeading.isHeading;

        if (isHeading) {
          const key = "heading_" + item.label;
          return (
            <ListSubheader
              key={key} // must have a non-undefined key
              style={{
                //see https://github.com/mui-org/material-ui/issues/18200
                pointerEvents: "none",
              }}
              disableSticky //https://stackoverflow.com/questions/59626747/react-material-ui-listitem-list-sticky-but-overlapping
            >
              {itemAsHeading.label}
            </ListSubheader>
          );
        } else {
          const key = toSafeKey(itemAsItem.id);
          const isInactiveValue = isInactiveValueSelected(
            itemAsItem.id,
            safeValue
          );
          return (
            (itemAsItem.active !== false || isInactiveValue) && (
              <MenuItem
                key={key} // must have a non-undefined key
                value={toSafeValue(itemAsItem.id)}
                disabled={itemAsItem.disabled}
                id={`${rootId}_${key}`}
              >
                {itemAsItem.label}&nbsp;
              </MenuItem>
            )
          );
        }
      })
    : [
        <MenuItem key={0} disabled>
          Please wait...
        </MenuItem>,
      ];
};

export const isInactiveValueSelected = (item: ItemIdType, safeValue: any) =>
  Array.isArray(safeValue)
    ? safeValue.find((x) => x.toString() === item?.toString())
    : item?.toString() === safeValue.toString();

export const handleChangeMultiple = (
  name: string | undefined,
  item: ItemType,
  currentValue: ItemIdType[],
  isDelete: boolean,
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
) => {
  if (!onChange) {
    return;
  }
  // we mimic the natural onChange shape of the underlying material-ui control,
  //  so build a shape that includes target:... etc. Also `type` is required so that
  //  `react-hook-form` processes the `onChange` argument like a material-ui argument,
  //  rather than in a primitive "the whole value I am getting back is the actual value" style
  //  see https://github.com/react-hook-form/react-hook-form/blob/3b880620fe2f2833501f17f15ea006d2fc0869fd/src/logic/getInputValue.ts#L8
  const nextValue = {
    type: true,
    name,
    target: {
      value: isDelete
        ? currentValue.filter((value) => value !== item.id)
        : [...currentValue, item.id],
    },
  };

  onChange((nextValue as unknown) as React.ChangeEvent<HTMLInputElement>);
};

export const getSelectedChildren = (
  value: ItemIdType[],
  children: (ItemType | HeadingType)[]
): ItemType[] => {
  const itemsOnly = children.filter(
    (item) => !(item as HeadingType).isHeading
  ) as ItemType[];

  return itemsOnly.filter((item) => value.includes(item.id));
};

export const renderChips = (
  selectedItems: ItemType[],
  id: string,
  isDisabled: boolean | undefined,
  onDelete: (item: ItemType) => void
) => (
  <div style={{ display: "flex", flexWrap: "wrap" }} role="list">
    {selectedItems.map((item) => (
      <React.Fragment key={toSafeKey(item.id)}>
        <Chip
          color="primary"
          label={<div style={{ whiteSpace: "normal" }}>{item.label}</div>}
          disabled={isDisabled}
          onDelete={() => onDelete(item)}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          id={`${id}_${item.id}_selected`}
          role="listitem"
          style={{ height: "100%", padding: "3px", margin: "5px 0px" }}
          deleteIcon={<Cancel id={`${id}-${item.id}-delete`} data-test="delete-chip" />}
        />
        &nbsp;
      </React.Fragment>
    ))}
  </div>
);

export const toSafeKey = (itemId: ItemIdType) =>
  typeof itemId === "boolean" ? String(itemId) : itemId;

// Material UI does not want items to have null values
export const toSafeValue = (itemId: ItemIdType) =>
  itemId === null
    ? undefined
    : typeof itemId === "boolean"
    ? String(itemId)
    : itemId;

// to stop errors if the value is known but the items have not loaded yet
//  (Material UI moans about value "foo" being applied to a select with no
//  item that has value="foo", but is happy to get given "" until items load)
export const convertNullOrUnreadyToEmptyString = (
  value: unknown,
  hasChildren: boolean
) => {
  return !hasChildren || value === undefined || value === null ? "" : value;
};

export const convertNullOrUnreadyToEmptyArray = (
  value: unknown,
  hasChildren: boolean
): ItemIdType[] => {
  return !hasChildren || value === undefined || value === null
    ? []
    : (value as ItemIdType[]);
};
